import { tEnglish as t } from "@buza/i18n";
import type { ApiResult } from "../api.js";

// The ZD screen error pattern, console side (S-062; spec 4.3 "Errors on every ZD screen", NFR-15;
// decision BD-63). Every ZD screen that sends a command uses these, so refusals look and behave
// the same everywhere:
//   - the reason from the rule that refused it is shown, with the rule ID for support;
//   - field reasons are shown next to their fields (refusal.fields);
//   - the screen keeps what was entered: a refusal never clears a form (the caller keeps its
//     state, and this component never touches it);
//   - a record changed by someone else shows "This record changed. Reload to continue." with a
//     Reload button, and nothing was overwritten;
//   - a lost connection shows "No connection. Your entries are kept. Try again."
// The server audits every refused attempt (command.ts); the screen does nothing extra for that.

export interface Refusal {
  kind: "rule" | "stale" | "offline";
  message: string;
  rule?: string;
  fields: Record<string, string>;
}

/** The refusal in a failed api() result; undefined when the result succeeded. */
export function toRefusal<T>(r: ApiResult<T>): Refusal | undefined {
  if (r.ok) return undefined;
  if (r.status === 0) return { kind: "offline", message: t("common.errors.no_connection"), fields: {} };
  const e = r.error;
  if (e?.code === "STALE_RECORD") {
    return { kind: "stale", message: t("common.errors.stale_record"), rule: e?.rule, fields: {} };
  }
  return { kind: "rule", message: e?.message ?? t("common.errors.validation"), rule: e?.rule, fields: e?.fields ?? {} };
}

/** The If-Match header for a command on a record the screen opened at this version (NFR-16). */
export const ifMatch = (version: number): Record<string, string> => ({ "If-Match": `"${version}"` });

export function RefusalNotice({ refusal, onReload }: { refusal: Refusal | undefined; onReload?: () => void }) {
  if (!refusal) return null;
  return (
    <div role="alert" className={`refusal refusal-${refusal.kind}`}>
      <p className="refusal-message">{refusal.message}</p>
      {refusal.kind === "rule" && refusal.rule && (
        <p className="refusal-rule">
          {t("console.common.refused_rule")} <code>{refusal.rule}</code>
        </p>
      )}
      {refusal.kind !== "offline" && <p className="refusal-kept">{t("console.common.entries_kept")}</p>}
      {refusal.kind === "stale" && onReload && (
        <button type="button" onClick={onReload}>
          {t("console.common.reload")}
        </button>
      )}
    </div>
  );
}

/** A field's reason from a refusal, shown under the field. */
export function FieldReason({ refusal, field }: { refusal: Refusal | undefined; field: string }) {
  const reason = refusal?.fields[field];
  if (!reason) return null;
  return (
    <p className="field-error" id={`${field}-reason`}>
      {reason}
    </p>
  );
}
